// components/admin/DeliveryAgentManagement.tsx - COMPLETE (with expiry-date fields added)
import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Search,
  ArrowBack,
  Email,
  Phone,
  LocationOn,
  Badge,
  Upload,
  DirectionsCar,
  CardMembership,
  CheckCircle,
  Cancel,
  HourglassEmpty,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListDeliveryAgentsQuery,
  useAddDeliveryAgentMutation,
  useUpdateDeliveryAgentMutation,
  useDeleteDeliveryAgentMutation,
  useApproveDeliveryAgentMutation,
  DeliveryAgent,
  AddDeliveryAgentRequest,
  UpdateDeliveryAgentRequest,
} from '../../features/admin/deliveryAgent.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

// Allow file fields to be either File (new upload), string (existing URL) or null
interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  password: string;
  address: string;
  nationality: string;
  id_no: string;
  id_front: File | string | null;
  id_back: File | string | null;
  id_validity?: string | null;                // NEW: id expiry (YYYY-MM-DD)
  vehicle_reg_no: string;
  vehicle_reg_front: File | string | null;
  vehicle_reg_back: File | string | null;
  vehicle_reg_validity?: string | null;       // NEW
  licence_no: string;
  licence_front: File | string | null;
  licence_back: File | string | null;
  licence_validity?: string | null;           // NEW
  passport_no: string;
  passport_front: File | string | null;
  passport_back: File | string | null;
  passport_validity?: string | null;          // NEW
  licence_status?: number;
  licence_reject_reason?: string;
  id_status?: number;
  id_reject_reason?: string;
  passport_status?: number;
  passport_reject_reason?: string;
  vehicle_reg_status?: number;
  vreg_reject_reason?: string;
}

interface FormErrors {
  [key: string]: string;
}

interface RejectDialogState {
  open: boolean;
  agentId: number | null;
  documentType: 'licence' | 'id' | 'passport' | 'vehicle_reg' | null;
  reason: string;
}

const DeliveryAgentManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0: All, 1: Approved, 2: Pending, 3: Rejected
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<DeliveryAgent | null>(null);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    password: '',
    address: '',
    nationality: '',
    id_no: '',
    id_front: null,
    id_back: null,
    id_validity: null,
    vehicle_reg_no: '',
    vehicle_reg_front: null,
    vehicle_reg_back: null,
    vehicle_reg_validity: null,
    licence_no: '',
    licence_front: null,
    licence_back: null,
    licence_validity: null,
    passport_no: '',
    passport_front: null,
    passport_back: null,
    passport_validity: null,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState>({
    open: false,
    agentId: null,
    documentType: null,
    reason: '',
  });

  const agentsPerPage = 10;

  const {
    data: agentsResponse,
    isLoading,
    error,
    isError,
    refetch
  } = useListDeliveryAgentsQuery();

  const [addDeliveryAgent] = useAddDeliveryAgentMutation();
  const [updateDeliveryAgent] = useUpdateDeliveryAgentMutation();
  const [deleteDeliveryAgent, { isLoading: isDeleting }] = useDeleteDeliveryAgentMutation();
  const [approveDeliveryAgent] = useApproveDeliveryAgentMutation();

  const deliveryAgents = agentsResponse?.data || [];

  // --- Helper for preview URLs ---
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  function makePreviewUrl(value?: string | null) {
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value; // absolute already
    if (/^data:/i.test(value)) return value; // data URI
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    const path = value.startsWith('/') ? value : `/${value}`;
    return base ? `${base}${path}` : value;
  }

  // Determine agent status based on all document statuses
  const getAgentOverallStatus = (agent: DeliveryAgent): number => {
    // Check if any document is rejected (2)
    if (
      agent.licence_status === 2 ||
      agent.id_status === 2 ||
      agent.passport_status === 2 ||
      agent.vehicle_reg_status === 2
    ) {
      return 2; // Rejected
    }
    
    // Check if all documents are approved (1)
    const allApproved = 
      (agent.licence_status === 1 || !agent.licence_no) &&
      (agent.id_status === 1 || !agent.id_no) &&
      agent.passport_status === 1 &&
      (agent.vehicle_reg_status === 1 || !agent.vehicle_reg_no);
    
    if (allApproved) {
      return 1; // Approved
    }
    
    return 0; // Pending
  };

  // Filter agents by tab
  const filteredAgentsByTab = useMemo(() => {
    if (tabValue === 0) return deliveryAgents; // All
    
    return deliveryAgents.filter((agent: DeliveryAgent) => {
      const status = getAgentOverallStatus(agent);
      if (tabValue === 1) return status === 1; // Approved
      if (tabValue === 2) return status === 0; // Pending
      if (tabValue === 3) return status === 2; // Rejected
      return true;
    });
  }, [deliveryAgents, tabValue]);

  // Apply search filter
  const filteredAgents = useMemo(() => {
    return filteredAgentsByTab.filter((agent: DeliveryAgent) =>
      !searchStr ||
      agent.first_name.toLowerCase().includes(searchStr.toLowerCase()) ||
      agent.last_name.toLowerCase().includes(searchStr.toLowerCase()) ||
      agent.email.toLowerCase().includes(searchStr.toLowerCase()) ||
      agent.contact.includes(searchStr) ||
      agent.id.toString().includes(searchStr)
    );
  }, [filteredAgentsByTab, searchStr]);

  // Count agents by status
  const approvedCount = deliveryAgents.filter((a: DeliveryAgent) => getAgentOverallStatus(a) === 1).length;
  const pendingCount = deliveryAgents.filter((a: DeliveryAgent) => getAgentOverallStatus(a) === 0).length;
  const rejectedCount = deliveryAgents.filter((a: DeliveryAgent) => getAgentOverallStatus(a) === 2).length;

  const totalRecords = filteredAgents.length;
  const totalPages = Math.ceil(totalRecords / agentsPerPage);
  const paginatedAgents = filteredAgents.slice(
    (page - 1) * agentsPerPage,
    page * agentsPerPage
  );

  const isEditing = Boolean(editingAgent);

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      contact: '',
      password: '',
      address: '',
      nationality: '',
      id_no: '',
      id_front: null,
      id_back: null,
      id_validity: null,
      vehicle_reg_no: '',
      vehicle_reg_front: null,
      vehicle_reg_back: null,
      vehicle_reg_validity: null,
      licence_no: '',
      licence_front: null,
      licence_back: null,
      licence_validity: null,
      passport_no: '',
      passport_front: null,
      passport_back: null,
      passport_validity: null,
      licence_status: 0,
      licence_reject_reason: '',
      id_status: 0,
      id_reject_reason: '',
      passport_status: 0,
      passport_reject_reason: '',
      vehicle_reg_status: 0,
      vreg_reject_reason: '',
    });
    setFormErrors({});
    setEditingAgent(null);
  };

  const handleAddAgent = () => {
    resetForm();
    setShowForm(true);
  };

  console.log("new")

  // --- Updated handleEditAgent mapping to prefer *_url and fallback to other fields ---
  const handleEditAgent = (agent: DeliveryAgent) => {
    setFormData({
      first_name: agent.first_name || '',
      last_name: agent.last_name || '',
      email: agent.email || '',
      contact: agent.contact || '',
      password: '',
      address: agent.address || '',
      nationality: agent.nationality || '',
      id_no: agent.id_no || '',
      id_front: (agent as any).id_front_url || (agent as any).id_front || null,
      id_back: (agent as any).id_back_url || (agent as any).id_back || null,
      id_validity: (agent as any).id_validity ? (agent as any).id_validity.split('T')[0] : null, // normalize to yyyy-mm-dd
      vehicle_reg_no: agent.vehicle_reg_no || '',
      vehicle_reg_front: (agent as any).vehicle_reg_front_url || (agent as any).vehicle_reg_front || null,
      vehicle_reg_back: (agent as any).vehicle_reg_back_url || (agent as any).vehicle_reg_back || null,
      vehicle_reg_validity: (agent as any).vehicle_reg_validity ? (agent as any).vehicle_reg_validity.split('T')[0] : null,
      licence_no: agent.licence_no || '',
      licence_front: (agent as any).licence_front_url || (agent as any).licence_front || null,
      licence_back: (agent as any).licence_back_url || (agent as any).licence_back || null,
      licence_validity: (agent as any).licence_validity ? (agent as any).licence_validity.split('T')[0] : null,
      passport_no: agent.passport_no || '',
      passport_front: (agent as any).passport_front_url || (agent as any).passport_front || null,
      passport_back: (agent as any).passport_back_url || (agent as any).passport_back || null,
      passport_validity: (agent as any).passport_validity ? (agent as any).passport_validity.split('T')[0] : null,
      licence_status: agent.licence_status || 0,
      licence_reject_reason: agent.licence_reject_reason || '',
      id_status: agent.id_status || 0,
      id_reject_reason: agent.id_reject_reason || '',
      passport_status: agent.passport_status || 0,
      passport_reject_reason: agent.passport_reject_reason || '',
      vehicle_reg_status: agent.vehicle_reg_status || 0,
      vreg_reject_reason: agent.vreg_reject_reason || '',
    });
    setEditingAgent(agent);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      // set a File object; existing URLs will remain strings
      setFormData(prev => ({ ...prev, [name]: files[0] }));
      if (formErrors[name]) {
        setFormErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.contact.trim()) newErrors.contact = 'Contact is required';
    if (!isEditing && !formData.password.trim()) newErrors.password = 'Password is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.nationality.trim()) newErrors.nationality = 'Nationality is required';
    if (!formData.passport_no.trim()) newErrors.passport_no = 'Passport number is required';

    // When adding new agent, require passport files. For edit, allow existing URL or new File.
    if (!isEditing && !formData.passport_front) newErrors.passport_front = 'Passport front is required';
    if (!isEditing && !formData.passport_back) newErrors.passport_back = 'Passport back is required';

    // Optionally validate date formats (if provided)
    // (We don't force expiry presence, just ensure format if present)
    ['id_validity', 'passport_validity', 'licence_validity', 'vehicle_reg_validity'].forEach((field) => {
      const v = (formData as any)[field];
      if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        newErrors[field] = 'Invalid date format (YYYY-MM-DD)';
      }
    });

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsFormSubmitting(true);

    try {
      let result;
      if (isEditing && editingAgent) {
        const updateData: any = {
          id: editingAgent.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          contact: formData.contact,
          address: formData.address,
          nationality: formData.nationality,
          id_no: formData.id_no,
          vehicle_reg_no: formData.vehicle_reg_no,
          licence_no: formData.licence_no,
          passport_no: formData.passport_no,
          // document status & reasons
          licence_status: formData.licence_status,
          licence_reject_reason: formData.licence_reject_reason,
          id_status: formData.id_status,
          id_reject_reason: formData.id_reject_reason,
          passport_status: formData.passport_status,
          passport_reject_reason: formData.passport_reject_reason,
          vehicle_reg_status: formData.vehicle_reg_status,
          vreg_reject_reason: formData.vreg_reject_reason,
          // NEW: expiry fields
          id_validity: formData.id_validity || null,
          passport_validity: formData.passport_validity || null,
          licence_validity: formData.licence_validity || null,
          vehicle_reg_validity: formData.vehicle_reg_validity || null,
        } as UpdateDeliveryAgentRequest;

        if (formData.password) updateData.password = formData.password;

        // Only attach files if they are new File objects (not existing URL strings)
        if (formData.id_front instanceof File) updateData.id_front = formData.id_front;
        if (formData.id_back instanceof File) updateData.id_back = formData.id_back;
        if (formData.vehicle_reg_front instanceof File) updateData.vehicle_reg_front = formData.vehicle_reg_front;
        if (formData.vehicle_reg_back instanceof File) updateData.vehicle_reg_back = formData.vehicle_reg_back;
        if (formData.licence_front instanceof File) updateData.licence_front = formData.licence_front;
        if (formData.licence_back instanceof File) updateData.licence_back = formData.licence_back;
        if (formData.passport_front instanceof File) updateData.passport_front = formData.passport_front;
        if (formData.passport_back instanceof File) updateData.passport_back = formData.passport_back;

        result = await updateDeliveryAgent(updateData).unwrap();
      } else {
        const addData: any = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          contact: formData.contact,
          password: formData.password,
          address: formData.address,
          nationality: formData.nationality,
          id_no: formData.id_no,
          vehicle_reg_no: formData.vehicle_reg_no,
          licence_no: formData.licence_no,
          passport_no: formData.passport_no,
          licence_status: formData.licence_status,
          licence_reject_reason: formData.licence_reject_reason,
          id_status: formData.id_status,
          id_reject_reason: formData.id_reject_reason,
          passport_status: formData.passport_status,
          passport_reject_reason: formData.passport_reject_reason,
          vehicle_reg_status: formData.vehicle_reg_status,
          vreg_reject_reason: formData.vreg_reject_reason,
          // NEW: expiry fields included in add
          id_validity: formData.id_validity || null,
          passport_validity: formData.passport_validity || null,
          licence_validity: formData.licence_validity || null,
          vehicle_reg_validity: formData.vehicle_reg_validity || null,
        } as AddDeliveryAgentRequest;

        if (formData.id_front instanceof File) addData.id_front = formData.id_front;
        if (formData.id_back instanceof File) addData.id_back = formData.id_back;
        if (formData.vehicle_reg_front instanceof File) addData.vehicle_reg_front = formData.vehicle_reg_front;
        if (formData.vehicle_reg_back instanceof File) addData.vehicle_reg_back = formData.vehicle_reg_back;
        if (formData.licence_front instanceof File) addData.licence_front = formData.licence_front;
        if (formData.licence_back instanceof File) addData.licence_back = formData.licence_back;
        if (formData.passport_front instanceof File) addData.passport_front = formData.passport_front;
        if (formData.passport_back instanceof File) addData.passport_back = formData.passport_back;

        result = await addDeliveryAgent(addData).unwrap();
      }

      setSnackbar({
        open: true,
        message: result.message || `Delivery agent ${isEditing ? 'updated' : 'added'} successfully!`,
        severity: 'success'
      });

      handleBackToList();
      refetch();
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error?.data?.message || error.message || `Error ${isEditing ? 'updating' : 'adding'} delivery agent.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (agent: DeliveryAgent) => {
    if (window.confirm(`Are you sure you want to delete ${agent.first_name} ${agent.last_name}?`)) {
      try {
        const result = await deleteDeliveryAgent({ id: agent.id }).unwrap();
        setSnackbar({
          open: true,
          message: result.message || 'Delivery agent deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: err?.data?.message || err?.message || 'Error deleting delivery agent.',
          severity: 'error'
        });
      }
    }
  };

  const handleApprove = async (agentId: number, documentType: 'licence' | 'id' | 'passport' | 'vehicle_reg' | null) => {
    try {
      const approveData: any = { id: agentId };
      
      if (documentType) {
        // Approve specific document
        approveData[`${documentType}_status`] = 1;
      } else {
        // Approve all documents
        approveData.licence_status = 1;
        approveData.id_status = 1;
        approveData.passport_status = 1;
        approveData.vehicle_reg_status = 1;
      }

      const result = await approveDeliveryAgent(approveData).unwrap();
      
      setSnackbar({
        open: true,
        message: result.message || `${documentType ? documentType.replace('_', ' ') : 'All documents'} approved successfully!`,
        severity: 'success'
      });
      
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Error approving document.',
        severity: 'error'
      });
    }
  };

  const handleOpenRejectDialog = (agentId: number, documentType: 'licence' | 'id' | 'passport' | 'vehicle_reg') => {
    setRejectDialog({
      open: true,
      agentId,
      documentType,
      reason: '',
    });
  };

  const handleCloseRejectDialog = () => {
    setRejectDialog({
      open: false,
      agentId: null,
      documentType: null,
      reason: '',
    });
  };

  const handleReject = async () => {
    if (!rejectDialog.reason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a rejection reason',
        severity: 'error'
      });
      return;
    }

    try {
      const rejectData: any = {
        id: rejectDialog.agentId,
        [`${rejectDialog.documentType}_status`]: 2,
        [`${rejectDialog.documentType}_reject_reason`]: rejectDialog.reason,
      };

      const result = await approveDeliveryAgent(rejectData).unwrap();
      
      setSnackbar({
        open: true,
        message: result.message || `${rejectDialog.documentType?.replace('_', ' ')} rejected`,
        severity: 'info'
      });
      
      handleCloseRejectDialog();
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Error rejecting document.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderDocumentStatus = (
    status?: number, 
    rejectReason?: string,
    agentId?: number,
    documentType?: 'licence' | 'id' | 'passport' | 'vehicle_reg',
    expiryDate?: string | null
  ) => {
    if (status === 1) {
      return (
        <Box>
          <Chip 
            label="Approved" 
            size="small" 
            icon={<CheckCircle sx={{ fontSize: 16 }} />} 
            sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '11px' }} 
          />
          {expiryDate && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6d7175' }}>
              Expires: {expiryDate}
            </Typography>
          )}
        </Box>
      );
    } else if (status === 2) {
      return (
        <Tooltip title={rejectReason || 'Rejected'} arrow>
          <Box>
            <Chip 
              label="Rejected" 
              size="small" 
              icon={<Cancel sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: '#ffd6d6', color: '#c41c1c', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}
            />
            {expiryDate && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6d7175' }}>
                Expires: {expiryDate}
              </Typography>
            )}
          </Box>
        </Tooltip>
      );
    } else {
      // Pending - show approve/reject buttons
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip 
            label="Pending" 
            size="small" 
            icon={<HourglassEmpty sx={{ fontSize: 16 }} />} 
            sx={{ bgcolor: '#fef7cd', color: '#7c6e0a', fontWeight: 600, fontSize: '11px' }} 
          />
          {agentId && documentType && (
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              <Tooltip title="Approve" arrow>
                <IconButton 
                  size="small" 
                  onClick={() => handleApprove(agentId, documentType)}
                  sx={{ 
                    color: '#00783e', 
                    p: 0.5,
                    '&:hover': { bgcolor: '#d1f4e0' }
                  }}
                >
                  <CheckCircle sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject" arrow>
                <IconButton 
                  size="small" 
                  onClick={() => handleOpenRejectDialog(agentId, documentType)}
                  sx={{ 
                    color: '#c41c1c', 
                    p: 0.5,
                    '&:hover': { bgcolor: '#ffd6d6' }
                  }}
                >
                  <Cancel sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {expiryDate && (
            <Typography variant="caption" sx={{ ml: 0.5, color: '#6d7175' }}>
              Expires: {expiryDate}
            </Typography>
          )}
        </Box>
      );
    }
  };

  const renderOverallStatus = (agent: DeliveryAgent) => {
    const status = getAgentOverallStatus(agent);
    
    if (status === 1) {
      return <Chip label="Approved" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 }} />;
    } else if (status === 2) {
      return <Chip label="Rejected" size=" " sx={{ bgcolor: '#ffd6d6', color: '#c41c1c', fontWeight: 600 }} />;
    } else {  
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip label="Pending" size="small" sx={{ bgcolor: '#fef7cd', color: '#7c6e0a', fontWeight: 600 }} />
          <Tooltip title="Approve All Documents" arrow>
            <IconButton  
              size="small" 
              onClick={() => handleApprove(agent.id, null)}
              sx={{ 
                color: '#00783e', 
                p: 0.5,
                '&:hover': { bgcolor: '#d1f4e0' }
              }}
            >
              <CheckCircle sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
  };

  // --- DocumentPreview component: handles File and string URLs robustly ---
  const DocumentPreview: React.FC<{ fileOrUrl: File | string | null; onRemove: () => void; label?: string }> = ({ fileOrUrl, onRemove, label }) => {
    const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
    const [imgError, setImgError] = React.useState(false);

   React.useEffect(() => {
  setImgError(false);
  // cleanup previous object URL
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    setObjectUrl(null);
  }

  if (!fileOrUrl) return;

  if (fileOrUrl instanceof File) {
    const url = URL.createObjectURL(fileOrUrl);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }

  return;
  // eslint-disable-next-line
}, [fileOrUrl]);

    if (!fileOrUrl) return null;

    const src = fileOrUrl instanceof File ? objectUrl : makePreviewUrl(fileOrUrl as string);

    return (
      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        {src && !imgError ? (
          <Box
            component="img"
            src={src as string}
            alt={label || 'Document'}
            onError={() => setImgError(true)}
            sx={{
              width: 96,
              height: 64,
              objectFit: 'cover',
              borderRadius: 2,
              border: '1px solid #e1e3e5',
            }}
          />
        ) : (
          <Box sx={{ width: 96, height: 64, borderRadius: 2, border: '1px solid #e1e3e5', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
            <Typography variant="caption" sx={{ color: '#6d7175', textAlign: 'center' }}>
              {fileOrUrl instanceof File ? fileOrUrl.name : 'Preview not available'}
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
            {fileOrUrl instanceof File ? fileOrUrl.name : 'View uploaded'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!(fileOrUrl instanceof File) && (
              <Button size="small" href={makePreviewUrl(fileOrUrl as string) || '#'} target="_blank" rel="noopener" sx={{ textTransform: 'none' }}>
                Open
              </Button>
            )}
            <Button size="small" onClick={onRemove} sx={{ textTransform: 'none' }}>
              Remove
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1800, mx: 'auto', p: 3 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Delivery Agents
                  </Typography>

                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddAgent}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add agent
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${deliveryAgents.length})`} />
                      <Tab label={`Approved (${approvedCount})`} />
                      <Tab label={`Pending (${pendingCount})`} />
                      <Tab label={`Rejected (${rejectedCount})`} />
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search agents"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load delivery agents. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && paginatedAgents.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No delivery agents found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first delivery agent'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && paginatedAgents.length > 0 && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      {/* Table Header */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '50px 80px 200px 200px 140px 140px 140px 140px 140px 150px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        minWidth: '1600px'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          No
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Name
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Email
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          License
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          ID Doc
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Passport
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Vehicle Reg
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Overall Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      {paginatedAgents.map((agent: DeliveryAgent, index: number) => (
                        <Box
                          key={agent.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 80px 200px 200px 140px 140px 140px 140px 140px 150px 100px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < paginatedAgents.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                            minWidth: '1600px'
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>
                            {(page - 1) * agentsPerPage + index + 1}
                          </Typography>

                          <Chip
                            label={`#${agent.id}`}
                            size="small"
                            sx={{
                              bgcolor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 600,
                              fontSize: '11px'
                            }}
                          />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: '#008060',
                                fontSize: '14px',
                                fontWeight: 600
                              }}
                            >
                              {((agent.first_name || '')[0] || '') + ((agent.last_name || '')[0] || '')}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                              {agent.first_name} {agent.last_name}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                            {agent.email}
                          </Typography>

                          <Box>
                            {renderDocumentStatus(agent.licence_status, agent.licence_reject_reason, agent.id, 'licence', (agent as any).licence_validity ? (agent as any).licence_validity.split('T')[0] : null)}
                          </Box>

                          <Box>
                            {renderDocumentStatus(agent.id_status, agent.id_reject_reason, agent.id, 'id', (agent as any).id_validity ? (agent as any).id_validity.split('T')[0] : null)}
                          </Box>

                          <Box>
                            {renderDocumentStatus(agent.passport_status, agent.passport_reject_reason, agent.id, 'passport', (agent as any).passport_validity ? (agent as any).passport_validity.split('T')[0] : null)}
                          </Box>

                          <Box>
                            {renderDocumentStatus(agent.vehicle_reg_status, agent.vreg_reject_reason, agent.id, 'vehicle_reg', (agent as any).vehicle_reg_validity ? (agent as any).vehicle_reg_validity.split('T')[0] : null)}
                          </Box>

                          <Box>
                            {renderOverallStatus(agent)}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditAgent(agent)}
                              sx={{
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(agent)}
                              disabled={isDeleting}
                              sx={{
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW - Keep existing form code but show previews for already uploaded docs
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Delivery Agents
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit delivery agent' : 'Add delivery agent'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        {/* Personal Information */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Personal information
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="first_name"
                                  label="First name"
                                  value={formData.first_name}
                                  onChange={handleInputChange}
                                  error={!!formErrors.first_name}
                                  helperText={formErrors.first_name}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="last_name"
                                  label="Last name"
                                  value={formData.last_name}
                                  onChange={handleInputChange}
                                  error={!!formErrors.last_name}
                                  helperText={formErrors.last_name}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="email"
                                  label="Email"
                                  type="email"
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  error={!!formErrors.email}
                                  helperText={formErrors.email}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Email fontSize="small" sx={{ color: '#6d7175' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="contact"
                                  label="Contact"
                                  value={formData.contact}
                                  onChange={handleInputChange}
                                  error={!!formErrors.contact}
                                  helperText={formErrors.contact}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Phone fontSize="small" sx={{ color: '#6d7175' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="nationality"
                                  label="Nationality"
                                  value={formData.nationality}
                                  onChange={handleInputChange}
                                  error={!!formErrors.nationality}
                                  helperText={formErrors.nationality}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="password"
                                  label={isEditing ? "Password (leave blank to keep current)" : "Password"}
                                  type="password"
                                  value={formData.password}
                                  onChange={handleInputChange}
                                  error={!!formErrors.password}
                                  helperText={formErrors.password}
                                  fullWidth
                                  required={!isEditing}
                                  disabled={isFormSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  name="address"
                                  label="Address"
                                  value={formData.address}
                                  onChange={handleInputChange}
                                  error={!!formErrors.address}
                                  helperText={formErrors.address}
                                  fullWidth
                                  required
                                  multiline
                                  rows={3}
                                  disabled={isFormSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LocationOn fontSize="small" sx={{ color: '#6d7175' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>

                        {/* Passport Information */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Passport information
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  name="passport_no"
                                  label="Passport number"
                                  value={formData.passport_no}
                                  onChange={handleInputChange}
                                  error={!!formErrors.passport_no}
                                  helperText={formErrors.passport_no}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Badge fontSize="small" sx={{ color: '#6d7175' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>

                              {/* Passport validity date */}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="passport_validity"
                                  label="Passport expiry"
                                  type="date"
                                  value={formData.passport_validity || ''}
                                  onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                  error={!!formErrors.passport_validity}
                                  helperText={formErrors.passport_validity}
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Box sx={{
                                  border: '2px dashed #c9cccf',
                                  borderRadius: '12px',
                                  p: 2,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  bgcolor: '#fafbfb',
                                  transition: 'all 0.2s',
                                  borderColor: formErrors.passport_back ? '#d32f2f' : '#c9cccf',
                                  '&:hover': {
                                    borderColor: formErrors.passport_back ? '#d32f2f' : '#202223',
                                    bgcolor: '#f6f6f7'
                                  }
                                }}
                                onClick={() => document.getElementById('passport-front')?.click()}>
                                  {formData.passport_front ? (
                                    <Box>
                                      {formData.passport_front instanceof File ? (
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#008060' }}>
                                            ✓ {formData.passport_front.name}
                                          </Typography>
                                          <Button
                                            size="small"
                                            sx={{ mt: 1, textTransform: 'none' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFormData(prev => ({ ...prev, passport_front: null }));
                                            }}
                                          >
                                            Remove
                                          </Button>
                                        </Box>
                                      ) : (
                                        <Box>
                                          <Box component="img" src={makePreviewUrl(formData.passport_front as string) || undefined} alt="passport front" sx={{ width: '120px', height: 80, objectFit: 'cover', borderRadius: 2, mb: 1, mx: 'auto' }} />
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                            Uploaded
                                          </Typography>
                                          <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, passport_front: null })); }}>
                                            Remove
                                          </Button>
                                        </Box>
                                      )}
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Upload sx={{ fontSize: 32, color: '#6d7175', mb: 1 }} />
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                        Upload passport front
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#6d7175' }}>
                                        Required *
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                {formErrors.passport_front && (
                                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1.5 }}>
                                    {formErrors.passport_front}
                                  </Typography>
                                )}
                                <input
                                  id="passport-front"
                                  type="file"
                                  name="passport_front"
                                  hidden
                                  accept="image/*,.pdf"
                                  onChange={handleFileChange}
                                  disabled={isFormSubmitting}
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Box sx={{
                                  border: '2px dashed #c9cccf',
                                  borderRadius: '12px',
                                  p: 2,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  bgcolor: '#fafbfb',
                                  transition: 'all 0.2s',
                                  borderColor: formErrors.passport_back ? '#d32f2f' : '#c9cccf',
                                  '&:hover': {
                                    borderColor: formErrors.passport_back ? '#d32f2f' : '#202223',
                                    bgcolor: '#f6f6f7'
                                  }
                                }}
                                onClick={() => document.getElementById('passport-back')?.click()}>
                                  {formData.passport_back ? (
                                    <Box>
                                      {formData.passport_back instanceof File ? (
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#008060' }}>
                                            ✓ {formData.passport_back.name}
                                          </Typography>
                                          <Button
                                            size="small"
                                            sx={{ mt: 1, textTransform: 'none' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFormData(prev => ({ ...prev, passport_back: null }));
                                            }}
                                          >
                                            Remove
                                          </Button>
                                        </Box>
                                      ) : (
                                        <Box>
                                          <Box component="img" src={makePreviewUrl(formData.passport_back as string) || undefined} alt="passport back" sx={{ width: '120px', height: 80, objectFit: 'cover', borderRadius: 2, mb: 1, mx: 'auto' }} />
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                            Uploaded
                                          </Typography>
                                          <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, passport_back: null })); }}>
                                            Remove
                                          </Button>
                                        </Box>
                                      )}
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Upload sx={{ fontSize: 32, color: '#6d7175', mb: 1 }} />
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                        Upload passport back
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#6d7175' }}>
                                        Required *
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                {formErrors.passport_back && (
                                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1.5 }}>
                                    {formErrors.passport_back}
                                  </Typography>
                                )}
                                <input
                                  id="passport-back"
                                  type="file"
                                  name="passport_back"
                                  hidden
                                  accept="image/*,.pdf"
                                  onChange={handleFileChange}
                                  disabled={isFormSubmitting}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>

                        {/* ID Document */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              ID document (Optional)
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  name="id_no"
                                  label="ID number"
                                  value={formData.id_no}
                                  onChange={handleInputChange}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                />
                              </Grid>

                              {/* ID validity */}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="id_validity"
                                  label="ID expiry"
                                  type="date"
                                  value={formData.id_validity || ''}
                                  onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                  error={!!formErrors.id_validity}
                                  helperText={formErrors.id_validity}
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<Upload />}
                                  disabled={isFormSubmitting}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#c9cccf',
                                    color: '#202223',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: '#202223',
                                      bgcolor: 'transparent',
                                    }
                                  }}
                                >
                                  {formData.id_front ? (formData.id_front instanceof File ? '✓ ID Front' : 'View ID front') : 'Upload ID front'}
                                  <input
                                    type="file"
                                    name="id_front"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                                <DocumentPreview fileOrUrl={formData.id_front} onRemove={() => setFormData(prev => ({ ...prev, id_front: null }))} label="ID Front" />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<Upload />}
                                  disabled={isFormSubmitting}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#c9cccf',
                                    color: '#202223',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: '#202223',
                                      bgcolor: 'transparent',
                                    }
                                  }}
                                >
                                  {formData.id_back ? (formData.id_back instanceof File ? '✓ ID Back' : 'View ID back') : 'Upload ID back'}
                                  <input
                                    type="file"
                                    name="id_back"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                                <DocumentPreview fileOrUrl={formData.id_back} onRemove={() => setFormData(prev => ({ ...prev, id_back: null }))} label="ID Back" />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>

                        {/* Vehicle Registration */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Vehicle registration (Optional)
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  name="vehicle_reg_no"
                                  label="Vehicle registration number"
                                  value={formData.vehicle_reg_no}
                                  onChange={handleInputChange}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <DirectionsCar fontSize="small" sx={{ color: '#6d7175' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>

                              {/* vehicle reg expiry */}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="vehicle_reg_validity"
                                  label="Vehicle reg expiry"
                                  type="date"
                                  value={formData.vehicle_reg_validity || ''}
                                  onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                  error={!!formErrors.vehicle_reg_validity}
                                  helperText={formErrors.vehicle_reg_validity}
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<Upload />}
                                  disabled={isFormSubmitting}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#c9cccf',
                                    color: '#202223',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: '#202223',
                                      bgcolor: 'transparent',
                                    }
                                  }}
                                >
                                  {formData.vehicle_reg_front ? (formData.vehicle_reg_front instanceof File ? '✓ Vehicle Reg Front' : 'View reg front') : 'Upload reg front'}
                                  <input
                                    type="file"
                                    name="vehicle_reg_front"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                                <DocumentPreview fileOrUrl={formData.vehicle_reg_front} onRemove={() => setFormData(prev => ({ ...prev, vehicle_reg_front: null }))} label="Vehicle Reg Front" />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<Upload />}
                                  disabled={isFormSubmitting}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#c9cccf',
                                    color: '#202223',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: '#202223',
                                      bgcolor: 'transparent',
                                    }
                                  }}
                                >
                                  {formData.vehicle_reg_back ? (formData.vehicle_reg_back instanceof File ? '✓ Vehicle Reg Back' : 'View reg back') : 'Upload reg back'}
                                  <input
                                    type="file"
                                    name="vehicle_reg_back"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                                <DocumentPreview fileOrUrl={formData.vehicle_reg_back} onRemove={() => setFormData(prev => ({ ...prev, vehicle_reg_back: null }))} label="Vehicle Reg Back" />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>

                        {/* Driver's License */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Driver's license (Optional)
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  name="licence_no"
                                  label="License number"
                                  value={formData.licence_no}
                                  onChange={handleInputChange}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <CardMembership fontSize="small" sx={{ color: '#6d7175' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>

                              {/* licence validity */}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  name="licence_validity"
                                  label="License expiry"
                                  type="date"
                                  value={formData.licence_validity || ''}
                                  onChange={handleInputChange}
                                  InputLabelProps={{ shrink: true }}
                                  fullWidth
                                  disabled={isFormSubmitting}
                                  error={!!formErrors.licence_validity}
                                  helperText={formErrors.licence_validity}
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<Upload />}
                                  disabled={isFormSubmitting}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#c9cccf',
                                    color: '#202223',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: '#202223',
                                      bgcolor: 'transparent',
                                    }
                                  }}
                                >
                                  {formData.licence_front ? (formData.licence_front instanceof File ? '✓ License Front' : 'View license front') : 'Upload license front'}
                                  <input
                                    type="file"
                                    name="licence_front"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                                <DocumentPreview fileOrUrl={formData.licence_front} onRemove={() => setFormData(prev => ({ ...prev, licence_front: null }))} label="License Front" />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  fullWidth
                                  startIcon={<Upload />}
                                  disabled={isFormSubmitting}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#c9cccf',
                                    color: '#202223',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: '#202223',
                                      bgcolor: 'transparent',
                                    }
                                  }}
                                >
                                  {formData.licence_back ? (formData.licence_back instanceof File ? '✓ License Back' : 'View license back') : 'Upload license back'}
                                  <input
                                    type="file"
                                    name="licence_back"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                  />
                                </Button>
                                <DocumentPreview fileOrUrl={formData.licence_back} onRemove={() => setFormData(prev => ({ ...prev, licence_back: null }))} label="License Back" />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', position: 'sticky', top: 16 }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Document Approval Status
                            </Typography>
                            
                            {/* License Status */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ color: '#6d7175', mb: 0.5, display: 'block' }}>
                                License Status
                              </Typography>
                              <Select
                                size="small"
                                fullWidth
                                value={formData.licence_status || 0}
                                onChange={(e) => setFormData(prev => ({ ...prev, licence_status: e.target.value as number }))}
                                disabled={isFormSubmitting || !formData.licence_no}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#c9cccf',
                                  },
                                  borderRadius: '8px',
                                }}
                              >
                                <MenuItem value={0}>Pending</MenuItem>
                                <MenuItem value={1}>Approved</MenuItem>
                                <MenuItem value={2}>Rejected</MenuItem>
                              </Select>
                              {formData.licence_status === 2 && (
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Rejection reason"
                                  value={formData.licence_reject_reason || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, licence_reject_reason: e.target.value }))}
                                  disabled={isFormSubmitting}
                                  sx={{ mt: 1 }}
                                />
                              )}
                              {formData.licence_validity && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6d7175' }}>
                                  License expires: {formData.licence_validity}
                                </Typography>
                              )}
                            </Box>

                            {/* ID Status */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ color: '#6d7175', mb: 0.5, display: 'block' }}>
                                ID Document Status
                              </Typography>
                              <Select
                                size="small"
                                fullWidth
                                value={formData.id_status || 0}
                                onChange={(e) => setFormData(prev => ({ ...prev, id_status: e.target.value as number }))}
                                disabled={isFormSubmitting || !formData.id_no}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#c9cccf',
                                  },
                                  borderRadius: '8px',
                                }}
                              >
                                <MenuItem value={0}>Pending</MenuItem>
                                <MenuItem value={1}>Approved</MenuItem>
                                <MenuItem value={2}>Rejected</MenuItem>
                              </Select>
                              {formData.id_status === 2 && (
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Rejection reason"
                                  value={formData.id_reject_reason || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, id_reject_reason: e.target.value }))}
                                  disabled={isFormSubmitting}
                                  sx={{ mt: 1 }}
                                />
                              )}
                              {formData.id_validity && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6d7175' }}>
                                  ID expires: {formData.id_validity}
                                </Typography>
                              )}
                            </Box>

                            {/* Passport Status */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ color: '#6d7175', mb: 0.5, display: 'block' }}>
                                Passport Status
                              </Typography>
                              <Select
                                size="small"
                                fullWidth
                                value={formData.passport_status || 0}
                                onChange={(e) => setFormData(prev => ({ ...prev, passport_status: e.target.value as number }))}
                                disabled={isFormSubmitting}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#c9cccf',
                                  },
                                  borderRadius: '8px',
                                }}
                              >
                                <MenuItem value={0}>Pending</MenuItem>
                                <MenuItem value={1}>Approved</MenuItem>
                                <MenuItem value={2}>Rejected</MenuItem>
                              </Select>
                              {formData.passport_status === 2 && (
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Rejection reason"
                                  value={formData.passport_reject_reason || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, passport_reject_reason: e.target.value }))}
                                  disabled={isFormSubmitting}
                                  sx={{ mt: 1 }}
                                />
                              )}
                              {formData.passport_validity && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6d7175' }}>
                                  Passport expires: {formData.passport_validity}
                                </Typography>
                              )}
                            </Box>

                            {/* Vehicle Registration Status */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ color: '#6d7175', mb: 0.5, display: 'block' }}>
                                Vehicle Registration Status
                              </Typography>
                              <Select
                                size="small"
                                fullWidth
                                value={formData.vehicle_reg_status || 0}
                                onChange={(e) => setFormData(prev => ({ ...prev, vehicle_reg_status: e.target.value as number }))}
                                disabled={isFormSubmitting || !formData.vehicle_reg_no}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#c9cccf',
                                  },
                                  borderRadius: '8px',
                                }}
                              >
                                <MenuItem value={0}>Pending</MenuItem>
                                <MenuItem value={1}>Approved</MenuItem>
                                <MenuItem value={2}>Rejected</MenuItem>
                              </Select>
                              {formData.vehicle_reg_status === 2 && (
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Rejection reason"
                                  value={formData.vreg_reject_reason || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, vreg_reject_reason: e.target.value }))}
                                  disabled={isFormSubmitting}
                                  sx={{ mt: 1 }}
                                />
                              )}
                              {formData.vehicle_reg_validity && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6d7175' }}>
                                  Vehicle reg expires: {formData.vehicle_reg_validity}
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                              <Typography variant="caption" sx={{ color: '#0d47a1', fontWeight: 500 }}>
                                ℹ️ Set the approval status for each document. Rejection requires a reason. Add expiry dates where applicable.
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Reject Dialog */}
      <Dialog 
        open={rejectDialog.open} 
        onClose={handleCloseRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#202223' }}>
          Reject {rejectDialog.documentType?.replace('_', ' ')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Please provide a reason for rejection..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={handleCloseRejectDialog}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#6d7175' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReject}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#c41c1c',
              '&:hover': { bgcolor: '#a01616' }
            }}
          >
            Reject Document
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryAgentManagement;
